import { ChevronDown, X } from 'lucide-react'
import type { ButtonHTMLAttributes, SelectHTMLAttributes } from 'react'
import { Icon } from '../../platform/icons/Icon'
import { Button } from '../../platform/primitives/Button'
import { useHitTarget } from '../../platform/responsive/hitTarget'

/**
 * The builder's ONE field skin is `rule-control` in rules.css: a text input and
 * a select share its height, radius, fill and boundary. Only the floor is
 * inline — 44px below 640px (M0-R29), and a caller cannot shrink it.
 */
export function useControlFloor(): number {
  return useHitTarget('panel')
}

/**
 * A native `<select>` in the field skin. The OS arrow is switched off in CSS
 * (`appearance: none`) and the chevron is drawn here, in a token colour — a
 * native control's own chrome carries no declaration a contrast sweep can read.
 * The list it opens is still the platform's, which is what makes it usable
 * with a keyboard and a screen reader for free.
 */
export function RuleSelect({ className, style, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  const minHeight = useControlFloor()
  return (
    <span className={['rule-select', className].filter(Boolean).join(' ')}>
      <select className="rule-control rule-select__control" style={{ ...style, minHeight }} {...rest}>
        {children}
      </select>
      <span className="rule-select__chevron" aria-hidden="true">
        <Icon glyph={ChevronDown} size={14} />
      </span>
    </span>
  )
}

/**
 * The quiet round × — a row's remove, and the dialog's close. The shared
 * `Button`, so its hover, focus ring and blocked look are the app's own
 * (a route stylesheet may not skin a disabled state — controlTokens.test.ts);
 * rules.css sets only its shape. `blocked` keeps it in the tab order and
 * announced as unavailable (`aria-disabled`), as the only row's remove is: a
 * rule keeps at least one condition and one action. The floor is on both axes.
 */
export function QuietIconButton({
  blocked = false,
  onClick,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { blocked?: boolean }) {
  const target = useHitTarget('panel')
  return (
    <Button
      variant="outline"
      size="panel"
      className={['rule-x', className].filter(Boolean).join(' ')}
      style={{ minWidth: target }}
      aria-disabled={blocked}
      onClick={(e) => {
        if (!blocked) onClick?.(e)
      }}
      {...rest}
    >
      <Icon glyph={X} size={14} />
    </Button>
  )
}
